import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Clear all stale Supabase auth tokens from localStorage
export const clearSupabaseStorage = () => {
  try {
    Object.keys(localStorage)?.forEach((key) => {
      if (key?.startsWith('sb-')) localStorage.removeItem(key);
    });
  } catch (_) {}
};

// Pre-flight: clear any stored session that is expired or missing a refresh token
// before the client is initialised, so Supabase never attempts a doomed refresh.
try {
  const storageKeys = Object.keys(localStorage)?.filter((k) => k?.startsWith('sb-') && k?.endsWith('-auth-token'));
  for (const key of storageKeys) {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!parsed) continue;

      // No refresh token at all — session is unusable
      if (!parsed?.refresh_token) {
        localStorage.removeItem(key);
        continue;
      }

      // Session access token has expired — the refresh token may also be stale.
      // Clear it so Supabase doesn't attempt a refresh that will fail with
      // "Invalid Refresh Token: Refresh Token Not Found".
      if (parsed?.expires_at) {
        const expiresAt = parsed?.expires_at * 1000;
        // If expired by more than 7 days, the refresh token is almost certainly invalid too
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        if (expiresAt < sevenDaysAgo) {
          localStorage.removeItem(key);
          continue;
        }
      }
    }
  }
} catch (_) {}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // PKCE: email link delivers ?code=xxx and the browser exchanges it using
    // a code_verifier kept in localStorage. Email-scanner prefetches (Gmail,
    // Microsoft Safe Links, Mimecast) can't redeem the code because they
    // don't have the verifier — fixes GT9JvrYq "invalid or expired link".
    flowType: 'pkce',
  }
});

// Listen for auth events and clear stale tokens on any sign-out or failed refresh
supabase?.auth?.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    clearSupabaseStorage();
  }
  if (event === 'TOKEN_REFRESHED' && !session) {
    clearSupabaseStorage();
  }
});
